'use client';

import React from 'react';
import { isTripsterUrl, decorateTripsterUrl } from '@/lib/affiliates/utils/tripster';
import { t } from '@/i18n/translations';
import { buildProviderLink } from '@/utils/blog/providerLinkHelpers';
import { getAffiliateMetadata, buildAffiliateSubId } from '@/lib/affiliates/metadata';
import { wrapAffiliateLink } from '@/lib/affiliates/proxy';
import AffiliateLink from '@/components/common/AffiliateLink';

// Числовая ячейка (цена/рейтинг/число) → правое выравнивание, как в GF-таблицах.
const isNumericText = (s) => typeof s === 'string' && /^[€$₽]?\s?[\d.,]+\s?(%|\$|€|₽|\/.*)?$/.test(s.trim());

// Денежная ячейка → подсветка как .tb-price (€, $, ₽ перед/после числа).
const isPriceText = (s) =>
    typeof s === 'string' && /[€$₽]/.test(s) && /\d/.test(s);

// Положительный маркер → .tb-yes (зелёный, жирный).
const YES_RE = /^(да|есть|yes|✓|✔|включено|included|опция|option)\b/i;
// Отрицательный / пустой маркер → .tb-no (приглушённый).
const NO_RE = /^(нет|no|✗|✕|—|–|-|n\/a|частично|partial)$/i;

// Извлекает текст из ячейки (поддержка старого формата — строка, и нового — {text, bold})
const getCellText = (cell) => {
    if (typeof cell === 'string') return cell;
    if (cell && typeof cell === 'object') return cell.text || '';
    return '';
};

const isCellBold = (cell) => typeof cell === 'object' && !!cell?.bold;

// Форматы {текст[КОД]} и {текст[КОД|URL]}.
// Первый резолвится через gptData, второй использует явно заданный deep link.
const TABLE_LINK_RE = /\{([^\[]+)\[([A-Za-z0-9_-]+)(?:\|([^\]]+))?\][\]\}]/g;

const placeholderKey = (code) => `[${String(code).toUpperCase().replace(/-/g, '_')}]`;

const resolvePlaceholderHref = (code, gptData, lang, postSlug) => {
    if (!gptData) return null;

    const placeholder = placeholderKey(code);
    const countrySlug = gptData._meta?.countrySlug;

    if (placeholder.startsWith('[AFF_')) {
        const provider = gptData.providers?.[placeholder]?.provider;
        if (!provider || provider.active === false) return null;
        return buildProviderLink(provider, lang, countrySlug, {
            pageSlug: postSlug,
            position: 'blog-table',
        });
    }

    if (placeholder.startsWith('[CITY_')) {
        const city = gptData.cities?.[placeholder]?.city;
        const targetCountry = city?.countrySlug || countrySlug;
        return city?.slug && targetCountry ? `/${lang}/${targetCountry}/${city.slug}` : null;
    }

    if (placeholder.startsWith('[PLACE_')) {
        const poi = gptData.pois?.[placeholder]?.poi;
        const targetCountry = poi?.countrySlug || countrySlug;
        return poi?.slug && poi?.citySlug && targetCountry
            ? `/${lang}/${targetCountry}/${poi.citySlug}/places/${poi.slug}`
            : null;
    }

    if (placeholder.startsWith('[AIRPORT_')) {
        const airport = gptData.airports?.[placeholder]?.airport;
        const targetCountry = airport?.countrySlug || countrySlug;
        return airport?.iata && targetCountry
            ? `/${lang}/${targetCountry}/airport/${String(airport.iata).toLowerCase()}`
            : null;
    }

    if (placeholder.startsWith('[POST_')) {
        const post = gptData.posts?.[placeholder]?.post;
        return post?.slug ? `/${lang}/blog/${post.slug}` : null;
    }

    if (placeholder.startsWith('[PAGE_')) {
        const service = gptData.services?.[placeholder]?.service;
        return service?.code && countrySlug ? `/${lang}/${countrySlug}/${service.code}` : null;
    }

    return null;
};

/**
 * Парсит текст ячейки и возвращает массив частей (строки + ссылки)
 * Если deep link плейсхолдеров нет — возвращает исходную строку
 */
export const parseCellLinks = (text, gptData, lang, postSlug) => {
    if (!text || typeof text !== 'string') return text;

    const parts = [];
    let lastIndex = 0;
    let match;

    // Сбрасываем lastIndex на случай повторного использования
    TABLE_LINK_RE.lastIndex = 0;

    while ((match = TABLE_LINK_RE.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        const explicitHref = match[3] || null;
        const resolvedHref = explicitHref || resolvePlaceholderHref(match[2], gptData, lang, postSlug);
        parts.push({
            text: match[1],
            href: resolvedHref,
            key: match.index,
            affiliate: match[2].toUpperCase().startsWith('AFF_'),
        });
        lastIndex = match.index + match[0].length;
    }

    if (parts.length === 0) return text;

    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts;
};

// true, если в тексте есть хотя бы один табличный link-плейсхолдер.
const hasTableLink = (text) => {
    if (!text || typeof text !== 'string') return false;
    TABLE_LINK_RE.lastIndex = 0;
    return TABLE_LINK_RE.test(text);
};

// true, если ячейка — ровно один плейсхолдер без текста вокруг. Только такая
// ячейка (и только в последней колонке) выглядит как кнопка-действие.
const isSoloLink = (text) => {
    if (!text || typeof text !== 'string') return false;
    TABLE_LINK_RE.lastIndex = 0;
    const match = TABLE_LINK_RE.exec(text);
    return !!match && match[0].trim() === text.trim();
};

/**
 * Рендерит ссылки из ячейки.
 *
 * `.tb-go` — это CTA-капс (моно 10px, uppercase, nowrap), он уместен только
 * когда ссылка И ЕСТЬ вся ячейка-действие в последней колонке («перейти →»).
 * Внутри фразы («Рядом с Сарандой») или в колонке-заголовке он превращал
 * название места в микро-капс посреди обычного текста — отсюда «странный
 * шрифт» в таблицах блога. Такие ссылки идут как `.tb-a`: наследуют
 * типографику ячейки, отличаются цветом и подчёркиванием.
 */
const CellLinks = ({ parsed, postSlug, lang, linkClass = 'tb-a' }) =>
    parsed.map((part, i) => {
        if (typeof part === 'string') return <React.Fragment key={i}>{part}</React.Fragment>;
        if (!part.href) return <React.Fragment key={part.key}>{part.text}</React.Fragment>;
        const href = isTripsterUrl(part.href) ? decorateTripsterUrl(part.href, postSlug) : part.href;

        // Партнёрская ссылка в таблице: тот же контракт, что у текстовых ссылок
        // (portableTextConfig) — /api/r-обёртка против адблокеров + affiliate_click.
        // До этого таблицы рендерили голый <a>: клики по VPN-таблицам не считались
        // и резались EasyList (аудит 2026-08-13, §7 — vertical=vpn 1 клик/90д).
        const meta = getAffiliateMetadata(href);
        if (meta) {
            const tracked = wrapAffiliateLink(href, {
                provider: meta.provider,
                subId: buildAffiliateSubId({ language: lang, pageSlug: postSlug, position: 'table' }),
            });
            return (
                <AffiliateLink
                    key={part.key}
                    href={tracked}
                    provider={meta.provider}
                    vertical={meta.vertical}
                    position="table"
                    label={part.text}
                    className={linkClass}
                >
                    {part.text}
                </AffiliateLink>
            );
        }

        const external = /^https?:\/\//i.test(href) || String(href).startsWith('/api/r?');
        return (
            <a
                key={part.key}
                className={linkClass}
                href={href}
                target={external ? '_blank' : undefined}
                rel={external ? `nofollow noopener noreferrer${part.affiliate ? ' sponsored' : ''}` : undefined}
            >
                {part.text}
            </a>
        );
    });

/** Рендерит содержимое ячейки: plain-текст или смесь текста и ссылок */
const CellContent = ({ text, postSlug, gptData, lang, linkClass }) => {
    const parsed = parseCellLinks(text, gptData, lang, postSlug);
    if (typeof parsed === 'string') return parsed;
    return <CellLinks parsed={parsed} postSlug={postSlug} lang={lang} linkClass={linkClass} />;
};

const SanityTable = ({ table, lang = 'ru', postSlug = '', hideTitle = false, gptData = null }) => {
    // Новая схема: title, headers, rows, caption
    // Старая схема: titleRu/titleEn, headersRu/headersEn, rowsRu/rowsEn, captionRu/captionEn
    const title = table?.title || (lang === 'ru' ? table?.titleRu : table?.titleEn);
    const headers = table?.headers || (lang === 'ru' ? table?.headersRu : table?.headersEn);
    const rows = table?.rows || (
        table?.useRussianRowsForEnglish && lang === 'en'
            ? table?.rowsRu
            : (lang === 'ru' ? table?.rowsRu : table?.rowsEn)
    );
    const caption = table?.caption || (lang === 'ru' ? table?.captionRu : table?.captionEn);

    if (!table || !headers || !rows || headers.length === 0 || rows.length === 0) {
        return null;
    }

    const ui = t(lang, 'miscUi');

    const lastCol = headers.length - 1;

    // Колонка числовая, если её заголовок или большинство ячеек выглядят как числа.
    const numericCol = headers.map((_, ci) => {
        const vals = rows.map((r) => getCellText(r.cells?.[ci])).filter(Boolean);
        if (vals.length === 0) return false;
        return vals.filter(isNumericText).length >= Math.ceil(vals.length * 0.6);
    });

    return (
        <figure className="gf-table-fig">
            {title && !hideTitle && <h3 className="gf-table-title">{title}</h3>}
            <div className="tb-scroll">
                <table className="tb">
                    <thead>
                        <tr>
                            {headers.map((header, index) => (
                                <th key={index} className={numericCol[index] ? 'num' : ''}>{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => {
                            const pick = !!(row?.pick || row?.recommended);
                            return (
                                <tr key={rowIndex} className={pick ? 'pick' : ''}>
                                    {row.cells && row.cells.map((cell, cellIndex) => {
                                        const text = getCellText(cell);
                                        const bold = isCellBold(cell);
                                        const num = numericCol[cellIndex];
                                        const linked = hasTableLink(text);
                                        // CTA-капс только для одиночной ссылки в последней колонке;
                                        // всё остальное — инлайновая ссылка в шрифте ячейки.
                                        const isCta = cellIndex === lastCol && cellIndex !== 0 && isSoloLink(text);
                                        const content = (
                                            <CellContent
                                                text={text}
                                                postSlug={postSlug}
                                                gptData={gptData}
                                                lang={lang}
                                                linkClass={isCta ? 'tb-go' : 'tb-a'}
                                            />
                                        );

                                        // Первая колонка — провайдер/строка-заголовок.
                                        if (cellIndex === 0) {
                                            return (
                                                <td key={cellIndex} className={num ? 'num' : ''} data-label={headers[cellIndex]}>
                                                    <div className="tb-prov">
                                                        <span className="wm">{content}</span>
                                                        {pick && <span className="pk">{ui.pick}</span>}
                                                    </div>
                                                </td>
                                            );
                                        }

                                        // Ячейка со ссылкой — без классификации по содержимому
                                        // (цена/да/нет к ней неприменимы).
                                        if (linked) {
                                            // Вправо — только настоящая кнопка-действие. Раньше `num`
                                            // вешался на любую ссылку в последней колонке, и колонка
                                            // выходила разнобоем: «Автобус» слева, «Автобус из
                                            // Саранды» справа.
                                            return (
                                                <td key={cellIndex} className={num || isCta ? 'num' : ''} data-label={headers[cellIndex]}>
                                                    {content}
                                                </td>
                                            );
                                        }

                                        // Классификация по содержимому → GF-классы ячейки.
                                        let cls = '';
                                        if (isPriceText(text)) cls = 'tb-price';
                                        else if (num) cls = 'tb-rate';
                                        else if (YES_RE.test(text.trim())) cls = 'tb-yes';
                                        else if (NO_RE.test(text.trim())) cls = 'tb-no';

                                        const inner = cls
                                            ? <span className={cls}>{content}</span>
                                            : content;

                                        return (
                                            <td key={cellIndex} className={num ? 'num' : ''} data-label={headers[cellIndex]}>
                                                {bold && !cls ? <strong>{inner}</strong> : inner}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {caption && <figcaption className="gf-table-cap">{caption}</figcaption>}
        </figure>
    );
};

export default SanityTable;
